import { cloneDeep } from "lodash"
import { AFFIRM_SHOP_URL } from "@/constants/index"
import {
  CHECKOUT,
  EDIT_CART,
  LOAD_MORE,
  NAVIGATION,
  ORDER_SUMMARY,
  PRODUCT_NAVIGATION,
  PROMO,
  SHIPPING,
  SAVE_AND_CONTINUE,
  CONTINUE,
  LANDING,
  CALL_SUPPORT_PHONE_NUMBER,
  HELP_SUPPORT,
  CUSTOMER_SUPPORT,
  PAYMENT_BILLING,
  CREDIT_CARD,
  PAYPAL,
  PAYMENTS,
  CHECKOUT_WITH_PAYPAL,
  CHECKOUT_WITH,
  CHECKOUT_PROCESS_BUTTON,
  CHECKOUT_PCIPAL,
  CREATE_A_QUICK_ACCOUNT,
  FORGOT_PASSWORD,
  PASSWORD_RESET,
  HOMEOWNER_ACCOUNT,
  PROFESSIONAL_ACCOUNT,
  SAVE_CHANGES,
  BILLING_INFORMATION,
  PLACE_ORDER,
  PAYMENT_METHOD,
  SIGN_IN,
  CITY_DROPDOWN,
  PROFESSIONAL,
  SELECT_BUSINESS_TYPE,
  SAVE_ADDRESS_TO_MY_ACCOUNT,
  CHECKBOX,
  SIGN_ME_UP_FOR_THE_E_NEWSLETTER,
  MOUSE_OVER,
  SHIPPING_RESTRICTIONS,
  RESEND_EMAIL,
  PAYPAL_ERROR,
  NOT_APPLICABLE,
  CONTACT_INFO,
  INITIATED,
  CHECKOUT_AS_GUEST,
  SUCCESS,
  CHECKOUT_WITH_ACCOUNT,
  EDIT,
  CREATE_ACCOUNT,
  DELIVERY_ADDRESS,
  CONTINUE_TO_SHIPPING_METHOD,
  FAILURE,
  VERIFY_ADDRESS,
  EDIT_SHIPPING_ADDRESS,
  ADD_NEW_DELIVERY_ADDRESS,
  CANCEL,
  SHIPPING_METHOD,
  VIEW_ITEM_INCLUDED,
  CONTINUE_TO_PAYMENT_SUCCESS,
  CONTINUE_TO_PAYMENT_FAILURE,
  CONTINUE_TO_PAYMENT,
  CHECKOUT_WITH_AFFIRM,
  AFFIRM,
  PAY_OVER_TIME_WITH_AFFIRM,
  SEE_IF_YOU_QUALIFY,
  CHECKOUT_WITH_CREDIT_CARD,
  CREDIT_CARD_ERROR,
  PREQUALIFY_TOOLTIP,
  PAYMENT,
  CONTINUE_TO_BILLING_ADDRESS,
  DISPLAYED,
  PAYPAL_EXPRESS_CHECKOUT,
} from "@/components/checkout/v2/analytics/constants"
import { findSelectParcelType } from "../DeliveryOptions/utils"
import {
  calculateCartData,
  getAnalyticsUrl,
  sanitizeTextForAnalytics,
  getUserAnalyticsObject,
  shaEncrypt,
  getCSTTimestamp,
} from "@/utils/helper"
import { store } from "@/store"
import { checkExpPayPal } from "@/utils/payment"

const getAnalyticsCartData = async cartObj => {
  if (
    !cartObj ||
    typeof cartObj !== "object" ||
    Object.keys(cartObj).length === 0
  ) {
    return null
  }

  try {
    return await calculateCartData({ cart: cartObj }, true)
  } catch (error) {
    console.warn("Cart calculation failed:", error)
    return null
  }
}

export const enterAddressManuallyAnalytics = (title = "") => {
  const eventInfo = getEventInfo(title?.toLowerCase(), DELIVERY_ADDRESS)
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addEmailAnalytics = async data => {
  const {
    isAuth,
    email,
    errors,
    type,
    saveChanges,
    isCreateAccount = false,
    isVerifyAddress = false,
    selectedAccount,
    newsletterSignup = false,
  } = data
  let internalLinkName =
    type === "edit" ? saveChanges.toLowerCase() : SAVE_AND_CONTINUE

  let eventAction
  let internalLinkType
  let internalLinkZoneName
  if (isCreateAccount) {
    eventAction = `${CHECKOUT}:${CONTACT_INFO}:${CREATE_ACCOUNT}:${
      selectedAccount === "homeowner" ? HOMEOWNER_ACCOUNT : PROFESSIONAL_ACCOUNT
    }:${CHECKOUT_WITH_ACCOUNT}`

    internalLinkType = `${CHECKOUT}:${
      selectedAccount === "homeowner" ? HOMEOWNER_ACCOUNT : PROFESSIONAL_ACCOUNT
    }`

    internalLinkZoneName = `${CHECKOUT}:${CONTACT_INFO}:${CREATE_ACCOUNT}`
    internalLinkName = `${CHECKOUT_WITH_ACCOUNT}`
  } else if (isVerifyAddress) {
    eventAction = `${CHECKOUT}:${SHIPPING}:${SAVE_CHANGES}`
    internalLinkType = `${CHECKOUT}:${NAVIGATION}`
    internalLinkZoneName = `${CHECKOUT}:${SHIPPING}`
  } else {
    eventAction = `${CHECKOUT}:${SHIPPING}:${
      type === "edit" ? SAVE_CHANGES : SAVE_AND_CONTINUE
    }`

    internalLinkType = `${CHECKOUT}:${NAVIGATION}`

    internalLinkZoneName = `${CHECKOUT}:${SHIPPING}`
  }

  const eventInfo = {
    eventAction,
    eventName: eventAction,
    eventType: NAVIGATION,
    eventMsg: !errors.length ? "n/a" : errors.join("|")?.toLowerCase(),
    eventStatus: !errors.length ? "success" : "failure",
    internalLinkName,
    internalLinkPosition: CHECKOUT,
    internalLinkType,
    internalLinkZoneName,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    ...(isCreateAccount &&
      !errors.length && { newsletterSignUp: newsletterSignup }),
  }

  const { adobeDataLayer: { getState } = {} } = window
  const page = (getState && getState("page")) || {}
  const cartObj = store?.getState()?.cart?.cart ?? {}
  const cartData = await getAnalyticsCartData(cartObj)
  page.cart = cartData?.cart || {}

  delete page.pageName
  delete page.pageUrl
  delete page.productInfo

  if (page?.pageInfo) {
    delete page?.pageInfo?.defaultImage
    delete page?.pageInfo?.imageId
    delete page?.pageInfo?.resourceType
    page.pageInfo.timestamp = getCSTTimestamp()
  }

  if (!isAuth && email) {
    shaEncrypt(email)
      .then(encryptedEmail => {
        localStorage.setItem("hashedEmail", encryptedEmail)
        page.user = getUserAnalyticsObject({ encryptedEmail })
        window.adobeDataLayer.push({
          event: "cmp:click",
          page: page,
          eventInfo,
        })
      })
      .catch(er => {
        window.adobeDataLayer.push({
          event: "cmp:click",
          page: page,
          eventInfo,
        })
      })
  } else {
    window.adobeDataLayer.push({
      event: "cmp:click",
      page: page,
      eventInfo,
    })
  }
}

export const createQuickAccountAnalytics = () => {
  const eventInfo = getEventInfo(CREATE_A_QUICK_ACCOUNT)
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const forgotPasswordAnalytics = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}:${FORGOT_PASSWORD}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}:${FORGOT_PASSWORD}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: FORGOT_PASSWORD,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const forgotPasswordModalAnalytics = ({
  status,
  isMailSent,
  message,
  i18n,
}) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}:${PASSWORD_RESET} ${status}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}:${PASSWORD_RESET} ${status}`,
    eventType: NAVIGATION,
    eventMsg:
      message ||
      (status === "failure"
        ? i18n.generalAuthenticationError?.toLowerCase()
        : i18n.mailSent?.toLowerCase()),
    eventStatus: status,
    internalLinkName: !isMailSent ? i18n.submit?.toLowerCase() : RESEND_EMAIL,
    internalLinkPosition: "auth modal",
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}:${PASSWORD_RESET}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

export const passwordResetCloseAnalytics = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${PASSWORD_RESET}:close`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${PASSWORD_RESET}:close`,
    eventType: NAVIGATION,
    internalLinkName: "close",
    internalLinkPosition: "auth modal",
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN}:${PASSWORD_RESET}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addLoginAnalytics = (status, errorsList) => {
  if (!window.adobeDataLayer) return

  let formattedErrorsList = errorsList
  if (status === "failure") {
    formattedErrorsList = errorsList.join("|")?.toLowerCase() || "n/a"
  }

  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN} ${status}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${SIGN_IN} ${status}`,
    eventType: NAVIGATION,
    eventMsg: status === "failure" ? formattedErrorsList : "n/a",
    eventStatus: status,
    internalLinkName: SIGN_IN,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }

  const userObj = getUserAnalyticsObject()
  pushAnalyticsObjToDataLayer(eventInfo, null, null, null, userObj)
}

const getEventInfo = (action, shippingSectionName = "") => {
  const sectionName = shippingSectionName || SHIPPING
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${sectionName}:${action}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${sectionName}:${action}`,
    eventType: NAVIGATION,
    internalLinkName: action,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${sectionName || NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${sectionName}`,
  }

  return eventInfo
}

export const addContinueToPaymentAnalytics = async ({
  selected = {},
  parcels = [],
  isXpp = false,
  isIscUser = false,
}) => {
  const parcelTypeName = findSelectParcelType(selected, parcels)

  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  let buttonType
  if (isIscUser) {
    buttonType = CONTINUE_TO_BILLING_ADDRESS + " " + SUCCESS
  } else if (isXpp) {
    buttonType = CONTINUE
  } else {
    buttonType = CONTINUE_TO_PAYMENT_SUCCESS
  }

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING_METHOD}:${buttonType}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${SHIPPING_METHOD}:${buttonType}`,
    eventStatus: SUCCESS,
    eventType: NAVIGATION,
    internalLinkName: isIscUser
      ? CONTINUE_TO_BILLING_ADDRESS
      : isXpp
      ? CONTINUE
      : CONTINUE_TO_PAYMENT,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${parcelTypeName} ${SHIPPING}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_METHOD}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const addContinueToPaymentFailureAnalytics = async ({
  selected = {},
  parcels = [],
}) => {
  const parcelTypeName = findSelectParcelType(selected, parcels)

  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING_METHOD}:${CONTINUE_TO_PAYMENT_FAILURE}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${SHIPPING_METHOD}:${CONTINUE_TO_PAYMENT_FAILURE}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: CONTINUE_TO_PAYMENT,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${parcelTypeName}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_METHOD}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const editCartAnalytics = cartPage => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${EDIT_CART}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${EDIT_CART}`,
    eventType: NAVIGATION,
    internalLinkName: EDIT_CART,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: cartPage,
    internalLinkZoneName: `${CHECKOUT}:${ORDER_SUMMARY}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const loadMoreAnalytics = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${LOAD_MORE}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${LOAD_MORE}`,
    eventType: NAVIGATION,
    internalLinkName: LOAD_MORE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const productClickAnalytics = (pdpUrl, productName, productInfo) => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${PRODUCT_NAVIGATION}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${PRODUCT_NAVIGATION}`,
    eventType: NAVIGATION,
    internalLinkName: sanitizeTextForAnalytics(productName) || "n/a",
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${PRODUCT_NAVIGATION}`,
    internalLinkURL: getAnalyticsUrl(pdpUrl),
    internalLinkZoneName: `${CHECKOUT}:${ORDER_SUMMARY}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, productInfo)
}

export const updatePromoAnalytics = async (status, cart, promo, action) => {
  const cartData = await getAnalyticsCartData(cart)
  const eventInfo = {
    eventAction: `${CHECKOUT}:${ORDER_SUMMARY}:${action} ${PROMO}`,
    eventName: `${CHECKOUT}:${ORDER_SUMMARY}:${action} ${PROMO}`,
    eventType: NAVIGATION,
    eventMsg:
      status === "success"
        ? action === "apply"
          ? "promocode applied successfully"
          : "promocode removed successfully"
        : status?.toLowerCase(),
    eventStatus: status === "success" ? "success" : "failure",
    internalLinkName: action,
    internalLinkPosition: `${CHECKOUT}`,
    internalLinkType: `${CHECKOUT}:${PROMO}`,
    internalLinkZoneName: `${CHECKOUT}:${ORDER_SUMMARY}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    promoCode: promo,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const addAnalyticsForCardPayment = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_CREDIT_CARD}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_CREDIT_CARD}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: CREDIT_CARD,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForErrorMessages = (
  eventMsg = "n/a",
  eventStatus = "n/a",
  isModalOpen = false,
  eventText = "place order",
  modalCloseText = "",
  errorCode = "",
  errorDesc = ""
) => {
  const isXpp = checkExpPayPal()
  const { checkout: { paymentMethodName = "" } = {} } = store?.getState() ?? {}
  const paymentType = isXpp
    ? PAYPAL_EXPRESS_CHECKOUT
    : paymentMethodName.toLocaleLowerCase()
  const extraText = isModalOpen ? ":modal:something went wrong" : ""
  const internalLinkTypeName = isModalOpen
    ? `${CHECKOUT}:${paymentType}`
    : `${CHECKOUT}:${PAYMENT_METHOD}:${paymentType}`
  const eventAction =
    modalCloseText === "close" || eventStatus === "n/a"
      ? `${CHECKOUT}:${PAYMENT_BILLING}${extraText}${modalCloseText}:${eventText}`
      : `${CHECKOUT}:${PAYMENT_BILLING}${extraText}${modalCloseText}:${eventText} ${FAILURE}`
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction,
    eventMsg,
    eventName: eventAction,
    eventStatus,
    eventType: NAVIGATION,
    internalLinkName: `${eventText}`,
    internalLinkPosition: `${CHECKOUT}${extraText}`,
    internalLinkType: `${internalLinkTypeName}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}${extraText}`,
    ...((errorCode || errorDesc) && {
      errorcode: errorCode,
      errordesc: errorDesc,
    }),
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, null, paymentType)
}

export const addAnalyticsForErrorCheckout = (
  event,
  customerCareNum = "n/a"
) => {
  event.preventDefault()
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${customerCareNum}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${customerCareNum}`,
    eventStatus: "n/a",
    eventType: NAVIGATION,
    internalLinkName: customerCareNum,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: (window.location.href = event.target.href),
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

const pushAnalyticsObjToDataLayer = (
  eventInfo = {},
  productInfo = {},
  cartObj = {},
  paymentMethodName = "",
  userObj = {}
) => {
  const page = cloneDeep(window.pageObj || {})

  delete page.pageName
  delete page.pageUrl
  delete page.productInfo

  if (page?.pageInfo) {
    delete page?.pageInfo?.contentIdentificationCategory
    delete page?.pageInfo?.contentIdentificationRoom
    delete page?.pageInfo?.contentIdentificationPurpose
    delete page?.pageInfo?.contentIdentificationCollection
    delete page?.pageInfo?.contentIdentificationDesignStyle
    delete page?.pageInfo?.contentIdentificationContentType

    page.category.room = NOT_APPLICABLE
    page.category.subCategoryName = CHECKOUT

    if (!page?.pageInfo?.referrer) {
      page.pageInfo.referrer =
        document.referrer || sessionStorage.getItem("previousPageUrl") || "n/a"
      sessionStorage.removeItem("previousPageUrl")
    }

    page.pageInfo.timestamp = getCSTTimestamp()
  }

  if (cartObj?.cart) {
    window.pageObj = window.pageObj || {}
    page.cart = cartObj.cart
    const cartItems = page?.cart?.item ?? []
    const cartItemStr = cartItems
      .map(item => item.productInfo?.productID ?? "")
      .filter(Boolean)
      .join(",")
    page.cartInfo = { cartItems: cartItemStr }
    window.pageObj.cart = cartObj.cart
    window.pageObj.cartInfo = { cartItems: cartItemStr }
  }

  if (paymentMethodName) {
    page.cart = {
      ...page.cart,
      payment: { paymentMethod: paymentMethodName },
    }
  }

  if (
    userObj &&
    typeof userObj === "object" &&
    Object.keys(userObj).length > 0
  ) {
    page.user = userObj
  }

  const eventData = {
    event: "cmp:click",
    eventInfo,
    page,
  }

  if (
    productInfo &&
    typeof productInfo === "object" &&
    Object.keys(productInfo).length > 0
  ) {
    eventData.productInfo = productInfo
  }

  window.adobeDataLayer.push(eventData)
}

export const addAnalyticsCallSupportNumber = e => {
  if (!e?.target?.innerText) return
  const innerText = e.target.innerText
  let eventInfo = {}
  const isEmail = innerText.toLowerCase().includes("email")
  const actionValue = isEmail
    ? innerText.toLowerCase()
    : CALL_SUPPORT_PHONE_NUMBER
  eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${LANDING}:${actionValue}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${LANDING}:${actionValue}`,
    eventStatus: "n/a",
    eventType: `${HELP_SUPPORT}`,
    internalLinkName: isEmail ? innerText.toLowerCase() : innerText,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:navigation`,
    internalLinkURL: isEmail ? e?.target?.href : `tel:${innerText}`,
    internalLinkZoneName: `${CHECKOUT}:${LANDING}:${CUSTOMER_SUPPORT}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const mouseOverTooltipAnalytics = (data = {}) => {
  const { type, i18n } = data
  const eventText = `cart:landing page:${
    type === i18n?.oversizedFragileDelivery
      ? type.toLowerCase().replace("/", " ")
      : `estimated ${type}`
  } mouseover`

  const eventLinkName =
    type === i18n?.oversizedFragileDelivery
      ? sanitizeTextForAnalytics(i18n?.oversizedDelivery)
          ?.split("  ")
          ?.join(" ")
      : i18n?.shippingTooltip.toLowerCase()

  const eventInfo = {
    eventAction: eventText,
    eventName: eventText,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName:
      type === "tax" ? i18n?.taxTooltip.toLowerCase() : eventLinkName,
    internalLinkPosition: "cart",
    internalLinkType: "cart:mouseover",
    internalLinkZoneName: "cart:landing page:order summary",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPaypal = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_PAYPAL}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_PAYPAL}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: PAYPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPaypalButton = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${PAYPAL} ${PAYMENTS}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${PAYPAL} ${PAYMENTS}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: PAYPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "false",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPciPal = (pciPalTxt = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${pciPalTxt}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${pciPalTxt}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: CHECKOUT_PCIPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForPciProcessBtnClick = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${CHECKOUT_PCIPAL}:${CHECKOUT_PROCESS_BUTTON}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH} ${CHECKOUT_PCIPAL}:${CHECKOUT_PROCESS_BUTTON}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "success",
    internalLinkName: CHECKOUT_PCIPAL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsVerifyAddress = (action = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${VERIFY_ADDRESS}:${action}`,
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${VERIFY_ADDRESS}:${action}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: action,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${VERIFY_ADDRESS}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsBillingInformationBtnClick = (btnTxt = "") => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${BILLING_INFORMATION}:${btnTxt} ${SUCCESS}`,
    eventMsg: "n/a",
    eventStatus: "success",
    eventName: `${CHECKOUT}:${BILLING_INFORMATION}:${btnTxt} ${SUCCESS}`,
    eventType: NAVIGATION,
    internalLinkName: btnTxt,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${BILLING_INFORMATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${BILLING_INFORMATION}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addPlaceOrderCCAnalytics = (
  paymentMethod = "n/a",
  status = "n/a",
  link,
  errorMsg = "",
  isValid = ""
) => {
  const eventInfo = {
    eventAction: isValid
      ? `${CHECKOUT}:${PAYMENT_BILLING}:${CREDIT_CARD_ERROR}`
      : `${CHECKOUT}:${PAYMENT_BILLING}:${PLACE_ORDER} ${SUCCESS}`,
    eventName: isValid
      ? `${CHECKOUT}:${PAYMENT_BILLING}:${CREDIT_CARD_ERROR}`
      : `${CHECKOUT}:${PAYMENT_BILLING}:${PLACE_ORDER} ${SUCCESS}`,
    eventType: NAVIGATION,
    eventMsg: errorMsg || "n/a",
    eventStatus: status || "n/a",
    internalLinkName: PLACE_ORDER,
    internalLinkPosition: `${CHECKOUT}`,
    internalLinkType: `${CHECKOUT}:${PAYMENT_METHOD}:${CREDIT_CARD}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: link
      ? (link.indexOf("http") === -1 ? window.location.origin : "") + link
      : "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, null, "Credit Card")
}

export const placeOrderClickFailed = (
  errorMsg,
  errorCode = "",
  errorDesc = ""
) => {
  const isXpp = checkExpPayPal()
  const { checkout: { paymentMethodName = "" } = {} } = store?.getState() ?? {}
  const paymentType =
    paymentMethodName.toLocaleLowerCase() === "card"
      ? CREDIT_CARD
      : isXpp
      ? PAYPAL_EXPRESS_CHECKOUT
      : paymentMethodName.toLocaleLowerCase()

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${PLACE_ORDER} ${FAILURE}`,
    eventMsg: errorMsg || "placeorder failed",
    errorcode: errorCode || "",
    errordesc: errorDesc || "",
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${PLACE_ORDER} ${FAILURE}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: PLACE_ORDER,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${PAYMENT_METHOD}:${paymentType}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, null, paymentType)
}

export const addAnalyticsOrderSummaryDropDown = (actionType = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY} ${actionType}`,
    eventName: `${CHECKOUT}:${LANDING}:${ORDER_SUMMARY} ${actionType}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: actionType,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${ORDER_SUMMARY}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addCheckoutSignInAnalyticsData = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}:${SIGN_IN}`,
    eventName: `${CHECKOUT}:${SHIPPING}:${SIGN_IN}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: SIGN_IN,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const paypalXPPErrorOnLanding = async data => {
  const { customErrorMsg } = data
  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:landing page:${PAYPAL_ERROR}`,
    eventMsg: customErrorMsg,
    eventName: `${CHECKOUT}:landing page:${PAYPAL_ERROR}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: PAYPAL_ERROR,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${SHIPPING}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:landing page`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const shippingErrorTaxCalculate = async data => {
  const { customErrorMsg } = data
  const { cart: { cart: cartObj = {} } = {} } = store?.getState() ?? {}
  const cartData = await getAnalyticsCartData(cartObj)

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}`,
    eventMsg: customErrorMsg?.toLowerCase(),
    eventName: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}`,
    eventStatus: "failure",
    eventType: NAVIGATION,
    internalLinkName: SAVE_AND_CONTINUE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, cartData)
}

export const addShippingAnalytics = (status, message, i18n) => {
  if (window.adobeDataLayer) {
    const eventInfo = getShippingEventInfo(CONTINUE_TO_SHIPPING_METHOD, true)
    if (status) {
      eventInfo.eventStatus = status
    }
    if (message) {
      if (
        message === "failed to set shipping address." ||
        message === "failed to get shipping options."
      ) {
        message = i18n.shippingProcessError?.toLowerCase()
      }
      eventInfo.eventMsg = message?.toLowerCase()
    }
    pushAnalyticsObjToDataLayer(eventInfo)
  }
}

const getShippingEventInfo = (type, needJSON = false) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${type} ${FAILURE}`,
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${type} ${FAILURE}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: FAILURE,
    internalLinkName: CONTINUE_TO_SHIPPING_METHOD,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  if (needJSON) {
    return eventInfo
  }
  return JSON.stringify(eventInfo)
}

export const addAnalyticsDeliveryComboSku = ({
  title = "",
  actionTitle = "",
}) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING_METHOD}:${VIEW_ITEM_INCLUDED}:${title}${actionTitle}`,
    eventName: `${CHECKOUT}:${SHIPPING_METHOD}:${VIEW_ITEM_INCLUDED}:${title}${actionTitle}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: actionTitle,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_METHOD}:${VIEW_ITEM_INCLUDED}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsBackOrderTooltip = ({
  title = "",
  tooltipMsg = "",
}) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING_METHOD}:${title}:${MOUSE_OVER}`,
    eventName: `${CHECKOUT}:${SHIPPING_METHOD}:${title}:${MOUSE_OVER}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: tooltipMsg,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_METHOD}:${title}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsShowHidePasswordClick = ({ btnClickAction = "" }) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${SIGN_IN}:${btnClickAction}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${SIGN_IN}:${btnClickAction}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: btnClickAction,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}: ${btnClickAction}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${SIGN_IN}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsCityClick = (cityName = "", selectedAccount = "") => {
  const selectedAccountType = selectedAccount
    ? `:${CREATE_A_QUICK_ACCOUNT}:${selectedAccount} account`
    : `:${SHIPPING}`
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${CITY_DROPDOWN}:${cityName}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${CITY_DROPDOWN}:${cityName}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: cityName,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}${selectedAccountType}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${CITY_DROPDOWN}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsBusinessTypeDropDownClick = (businessType = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CREATE_A_QUICK_ACCOUNT}: ${PROFESSIONAL}:${businessType}`,
    eventName: `${CHECKOUT}:${CREATE_A_QUICK_ACCOUNT}: ${PROFESSIONAL}:${businessType}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: businessType,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${SELECT_BUSINESS_TYPE}:${businessType}`,
    internalLinkZoneName: `${CHECKOUT}:${CREATE_A_QUICK_ACCOUNT}: ${PROFESSIONAL}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsSaveAddressClick = (checkboxAction = "") => {
  const selectedText = checkboxAction ? "selection" : "unselection"
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}: ${SAVE_ADDRESS_TO_MY_ACCOUNT}:${CHECKBOX} ${selectedText}`,
    eventName: `${CHECKOUT}:${SHIPPING}: ${SAVE_ADDRESS_TO_MY_ACCOUNT}:${CHECKBOX} ${selectedText}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${SAVE_ADDRESS_TO_MY_ACCOUNT}:${CHECKBOX} ${selectedText}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${CHECKBOX} ${selectedText}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${SAVE_ADDRESS_TO_MY_ACCOUNT}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsNewsletterSignupClick = (checkboxAction = "") => {
  const selectedText = checkboxAction ? "selection" : "unselection"
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING}:${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}:${CHECKBOX} ${selectedText}`,
    eventName: `${CHECKOUT}:${SHIPPING}:${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}:${CHECKBOX} ${selectedText}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}:${CHECKBOX} ${selectedText}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${CHECKBOX} ${selectedText}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING}: ${SIGN_ME_UP_FOR_THE_E_NEWSLETTER}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsRemoveItems = async (product = []) => {
  const cartObj = store?.getState()?.cart?.cart
  const cartData = await getAnalyticsCartData(cartObj)
  const productArrayLists = product ? product.length > 1 : false
  const prodcutLists = []

  if (product?.length) {
    for (const prodInfo of product) {
      const sku = prodInfo?.sku?.toLowerCase()
      if (!sku) continue

      const matchingSku = cartObj?.cart?.item?.find(
        prodMatch => prodMatch.productInfo?.productID?.toLowerCase() === sku
      )

      if (matchingSku?.productInfo) {
        prodcutLists.push(matchingSku.productInfo)
      }
    }
  }

  const removeText = productArrayLists ? "remove items" : "remove item"
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}:${SHIPPING_RESTRICTIONS}:${removeText}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${SHIPPING}:${SAVE_AND_CONTINUE}:${SHIPPING_RESTRICTIONS}:${removeText}`,
    eventType: NAVIGATION,
    internalLinkName: removeText,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_RESTRICTIONS}`,
  }

  const productInfoJSON =
    prodcutLists?.length > 0 ? { productInfo: prodcutLists } : null
  pushAnalyticsObjToDataLayer(eventInfo, productInfoJSON, cartData)
}

export const addAnalyticsCheckoutIntitiated = (accountType = "") => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${accountType} ${INITIATED}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${accountType} ${INITIATED}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${accountType}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsGuestCheckoutSuccess = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${CHECKOUT_AS_GUEST} ${SUCCESS}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${CHECKOUT_AS_GUEST} ${SUCCESS}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: SUCCESS,
    internalLinkName: CHECKOUT_AS_GUEST,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForTooltipMouseOver = (
  tooltipMsg = "",
  accountType = ""
) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${CREATE_ACCOUNT}:${accountType?.toLocaleLowerCase()} ${MOUSE_OVER}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${CREATE_ACCOUNT}:${accountType?.toLocaleLowerCase()} ${MOUSE_OVER}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: tooltipMsg?.toLocaleLowerCase(),
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}:${CREATE_ACCOUNT}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsObjForEdit = async (editType = "") => {
  const EDIT_TYPE =
    editType?.toLocaleLowerCase() === PAYMENT
      ? PAYMENT_BILLING
      : editType?.toLocaleLowerCase()
  const eventInfo = {
    eventAction: `${CHECKOUT}:${EDIT_TYPE}:${EDIT}`,
    eventName: `${CHECKOUT}:${EDIT_TYPE}:${EDIT}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: EDIT,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${EDIT_TYPE}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsObjForSaveAndContinue = async (
  labelForIscBtn = ""
) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${CONTACT_INFO}:${
      labelForIscBtn || SAVE_AND_CONTINUE
    } ${SUCCESS}`,
    eventName: `${CHECKOUT}:${CONTACT_INFO}:${
      labelForIscBtn || SAVE_AND_CONTINUE
    } ${SUCCESS}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: SUCCESS,
    internalLinkName: labelForIscBtn || SAVE_AND_CONTINUE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${CONTACT_INFO}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  const userObj = getUserAnalyticsObject()
  pushAnalyticsObjToDataLayer(eventInfo, {}, {}, "", userObj)
}

export const addAnalyticsContiueToShippingMethod = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${CONTINUE_TO_SHIPPING_METHOD} ${SUCCESS}`,
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${CONTINUE_TO_SHIPPING_METHOD} ${SUCCESS}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: SUCCESS,
    internalLinkName: `${CONTINUE_TO_SHIPPING_METHOD}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsAddressEdit = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${EDIT}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${EDIT}`,
    eventType: NAVIGATION,
    internalLinkName: EDIT,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsContinueToShippingMethod = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${EDIT_SHIPPING_ADDRESS}:${CONTINUE_TO_SHIPPING_METHOD}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${EDIT_SHIPPING_ADDRESS}:${CONTINUE_TO_SHIPPING_METHOD}`,
    eventType: NAVIGATION,
    internalLinkName: CONTINUE_TO_SHIPPING_METHOD,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS}:${EDIT_SHIPPING_ADDRESS}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${EDIT_SHIPPING_ADDRESS}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsNewAddressClick = (label = "") => {
  const addressLabel = label ? `:${label} ` : ""
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}${addressLabel}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}${addressLabel}`,
    eventType: NAVIGATION,
    internalLinkName: addressLabel || ADD_NEW_DELIVERY_ADDRESS,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS} ${
      addressLabel ? `:${ADD_NEW_DELIVERY_ADDRESS}` : ""
    }`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}${
      addressLabel ? `:${ADD_NEW_DELIVERY_ADDRESS}` : ""
    }`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsNewSaveAddressClick = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}:${SAVE_AND_CONTINUE}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}:${SAVE_AND_CONTINUE}`,
    eventType: NAVIGATION,
    internalLinkName: SAVE_AND_CONTINUE,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsCancelAddressClick = () => {
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}:${CANCEL}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}:${CANCEL}`,
    eventType: NAVIGATION,
    internalLinkName: CANCEL,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${ADD_NEW_DELIVERY_ADDRESS}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsProductListToggle = action => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${SHIPPING_METHOD}:${VIEW_ITEM_INCLUDED}:${action}`,
    eventName: `${CHECKOUT}:${SHIPPING_METHOD}:${VIEW_ITEM_INCLUDED}:${action}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: action,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_METHOD}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsShippingMethodProductClick = ({
  productName,
  pdpUrl,
  productInfo,
}) => {
  const productInfoObj = JSON.parse(productInfo)?.productInfo || {}
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${SHIPPING_METHOD}:${PRODUCT_NAVIGATION}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
    eventName: `${CHECKOUT}:${SHIPPING_METHOD}:${PRODUCT_NAVIGATION}`,
    eventType: NAVIGATION,
    internalLinkName: sanitizeTextForAnalytics(productName) || "n/a",
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: getAnalyticsUrl(pdpUrl),
    internalLinkZoneName: `${CHECKOUT}:${SHIPPING_METHOD}`,
  }
  pushAnalyticsObjToDataLayer(eventInfo, productInfoObj)
}

export const addAnalyticsForAffirmPayment = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_AFFIRM}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${CHECKOUT_WITH_AFFIRM}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: AFFIRM,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsForAffirmPaymentButton = type => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${PAY_OVER_TIME_WITH_AFFIRM}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${PAY_OVER_TIME_WITH_AFFIRM}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${PAY_OVER_TIME_WITH_AFFIRM}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: AFFIRM_SHOP_URL,
    clickInternalLinks: "false",
  }
  pushAnalyticsObjToDataLayer(eventInfo, "cmp:click")
}

export const addServiceAnalyticsData = (type, link) => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${type}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${type}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: type,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: link,
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo, "cmp:click")
}

export const addAnalyticsForAffirmQualifyNow = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${AFFIRM}:${SEE_IF_YOU_QUALIFY}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${AFFIRM}:${SEE_IF_YOU_QUALIFY}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${SEE_IF_YOU_QUALIFY}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: AFFIRM_SHOP_URL,
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo, "cmp:click")
}

export const addAnalyticsForAffirmTooltip = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}:${AFFIRM}:${PREQUALIFY_TOOLTIP} ${MOUSE_OVER}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}:${AFFIRM}:${PREQUALIFY_TOOLTIP} ${MOUSE_OVER}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${PREQUALIFY_TOOLTIP}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo, "cmp:click")
}

export const addPlaceOrderDataLayer = async (
  link,
  isModalOpen = false,
  paymentMethod = "",
  status = "n/a"
) => {
  const extraText = isModalOpen ? ":modal:something went wrong" : ""
  const isXpp = checkExpPayPal()
  const paymentType = isXpp
    ? PAYPAL_EXPRESS_CHECKOUT
    : paymentMethod.toLocaleLowerCase()
  const internalLinkTypeName = isModalOpen
    ? `${CHECKOUT}:${paymentType}`
    : `${CHECKOUT}:${PAYMENT_METHOD}:${paymentType}`
  const eventInfo = {
    eventAction: `${CHECKOUT}:${PAYMENT_BILLING}${extraText}:${PLACE_ORDER} ${SUCCESS}`,
    eventName: `${CHECKOUT}:${PAYMENT_BILLING}${extraText}:${PLACE_ORDER} ${SUCCESS}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: status || "n/a",
    internalLinkName: PLACE_ORDER,
    internalLinkPosition: `${CHECKOUT}${extraText}`,
    internalLinkType: `${internalLinkTypeName}`,
    internalLinkZoneName: `${CHECKOUT}:${PAYMENT_BILLING}${extraText}`,
    internalLinkURL: link
      ? (link.indexOf("http") === -1 ? window.location.origin : "") + link
      : "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo, null, null, paymentType)
}

export const addCheckoutStepDisplayedAnalytics = stepLabel => {
  const getStepNameForAnalytics = label => {
    const lowerLabel = label?.toLowerCase() || ""

    if (lowerLabel.includes("contact")) return CONTACT_INFO
    if (lowerLabel.includes("shipping")) return SHIPPING_METHOD
    if (lowerLabel.includes("delivery")) return DELIVERY_ADDRESS
    if (lowerLabel.includes("billing")) return BILLING_INFORMATION
    if (lowerLabel.includes("payment")) return PAYMENT_BILLING

    return null
  }

  const stepName = getStepNameForAnalytics(stepLabel)
  if (!stepName) return

  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `${CHECKOUT}:${stepName}:${DISPLAYED}`,
    eventMsg: "n/a",
    eventName: `${CHECKOUT}:${stepName}:${DISPLAYED}`,
    eventStatus: "n/a",
    eventType: NAVIGATION,
    internalLinkName: `${
      stepName === PAYMENT_BILLING ? PAYMENT_METHOD : stepName
    } ${DISPLAYED}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${NAVIGATION}`,
    internalLinkURL: "n/a",
    internalLinkZoneName: `${CHECKOUT}:${stepName}`,
  }

  pushAnalyticsObjToDataLayer(eventInfo)
}

export const addAnalyticsContiueToShippingMethodError = () => {
  const eventInfo = {
    eventAction: `${CHECKOUT}:${DELIVERY_ADDRESS}:${SAVE_AND_CONTINUE} ${FAILURE}`,
    eventName: `${CHECKOUT}:${DELIVERY_ADDRESS}:${SAVE_AND_CONTINUE} ${FAILURE}`,
    eventType: NAVIGATION,
    eventMsg: "n/a",
    eventStatus: FAILURE,
    internalLinkName: `${SAVE_AND_CONTINUE}`,
    internalLinkPosition: CHECKOUT,
    internalLinkType: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
    internalLinkZoneName: `${CHECKOUT}:${DELIVERY_ADDRESS}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  pushAnalyticsObjToDataLayer(eventInfo)
}
